import { useEffect, useState } from 'react';

/**
 * Wait after the load event before secondary work starts. The browser is often idle much earlier, while the first
 * screen still waits for its data (network): an idle callback alone fired in that gap and put the field metrics and
 * the alert counter in the way of the panel's indicators (Lighthouse, phone profile).
 */
export const AFTER_LOAD_MS = 2000;

/**
 * Runs `fn` once the page has loaded, AFTER_LOAD_MS have passed and the browser is idle (at most `timeout` ms later).
 * For work that must not compete with the first screen on a slow phone: field metrics, secondary requests. (Not the
 * service worker: see main.tsx.) Safari has no requestIdleCallback: the timer alone decides there. Returns a cancel
 * function.
 */
export function whenIdle(fn: () => void, timeout = 3000): () => void {
  let cancelled = false;
  let cancelInner = () => {};
  const idle = () => {
    if (cancelled) return;
    if (typeof requestIdleCallback === 'function') {
      const id = requestIdleCallback(() => !cancelled && fn(), { timeout });
      cancelInner = () => cancelIdleCallback(id);
    } else fn();
  };
  const schedule = () => {
    if (cancelled) return;
    const id = setTimeout(idle, AFTER_LOAD_MS);
    cancelInner = () => clearTimeout(id);
  };
  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule, { once: true });
  return () => {
    cancelled = true;
    window.removeEventListener('load', schedule);
    cancelInner();
  };
}

let reached = false;

/** False until whenIdle fires once in this page load; true at once for components mounted after that. */
export function useIdle(timeout?: number): boolean {
  const [idle, setIdle] = useState(reached);
  useEffect(() => {
    if (idle) return;
    return whenIdle(() => {
      reached = true;
      setIdle(true);
    }, timeout);
  }, [idle, timeout]);
  return idle;
}
